import ApiService from '@/service'
import { SearchParams } from '@/types'
import { moduleActionContext } from '@/vuex'
import type {
  ApiSchema,
  PolicySearchItem,
  RuleSearchItem,
  SearchResults,
  UserFunctionSearchItem,
} from '@klarna/cbrms-policy-rules-manager-client-ts/api'
import { defineActions, defineModule, defineMutations } from 'direct-vuex'

export const searchActionContext = (context: any) => moduleActionContext(context, searchModule)

interface SearchState {
  policiesSearchResults: PolicySearchItem[]
  rulesSearchResults: RuleSearchItem[]
  userFunctionsSearchResults: UserFunctionSearchItem[]
  searchResults: SearchResults
  searchResultsStatus: 'idle' | 'loading' | 'success'
  searchQuery: string | null
}

type ModuleSearchParams = {
  q: string
  api?: ApiSchema
  limit?: number
  start?: number
}

const searchModule = defineModule({
  namespaced: true,

  state: {
    policiesSearchResults: {},
    rulesSearchResults: {},
    userFunctionsSearchResults: {},
    searchResults: {},
    searchResultsStatus: 'idle',
    searchQuery: '',
  } as SearchState,

  mutations: defineMutations<SearchState>()({
    SET_POLICIES_SEARCH_RESULTS(state: SearchState, results: PolicySearchItem[]) {
      state.policiesSearchResults = results
    },
    SET_RULES_SEARCH_RESULTS(state: SearchState, results: RuleSearchItem[]) {
      state.rulesSearchResults = results
    },
    SET_USER_FUNCTIONS_SEARCH_RESULTS(state: SearchState, results: UserFunctionSearchItem[]) {
      state.userFunctionsSearchResults = results
    },
    SET_SEARCH_RESULTS(state: SearchState, searchResults: SearchResults) {
      state.searchResults = searchResults
    },
    SET_SEARCH_RESULTS_STATUS(state: SearchState, status: SearchState['searchResultsStatus']) {
      state.searchResultsStatus = status
    },
    SET_SEARCH_QUERY(state: SearchState, query: string | null) {
      state.searchQuery = query
    },
  }),
  actions: defineActions({
    async GET_POLICIES_SEARCH_RESULTS(
      ctx,
      { q = '', limit, start }: ModuleSearchParams
    ): Promise<{ nextPageExists: boolean; policies: PolicySearchItem[] }> {
      const { commit } = searchActionContext(ctx)
      const { data: results } = await ApiService.getSearchResults(
        q,
        undefined,
        'policy',
        undefined,
        undefined,
        start,
        limit
      )
      const { policies, res } = results
      const totalCount = res.aggregations.entity_type.buckets.length
        ? res.aggregations.entity_type.buckets[0].doc_count
        : 0
      const nextPageExists = (start || 0) + policies.length < totalCount
      commit.SET_POLICIES_SEARCH_RESULTS(results.policies)

      return {
        nextPageExists,
        policies,
      }
    },
    async GET_RULES_SEARCH_RESULTS(
      ctx,
      { q = '', api, limit, start }: ModuleSearchParams
    ): Promise<{
      rules: RuleSearchItem[]
      nextPageExists: boolean
    }> {
      const { commit } = searchActionContext(ctx)
      const { data: results } = await ApiService.getSearchResults(q, api, 'rule', undefined, undefined, start, limit)

      const { rules, res } = results
      const totalCount = res.aggregations.entity_type.buckets.length
        ? res.aggregations.entity_type.buckets[0].doc_count
        : 0
      const nextPageExists = (start || 0) + rules.length < totalCount

      commit.SET_RULES_SEARCH_RESULTS(results.rules)
      return {
        rules,
        nextPageExists,
      }
    },
    async GET_SEARCH_RESULTS(
      ctx,
      {
        q = '',
        api,
        entityType,
        deploymentRegion,
        teamName,
        start,
        size,
        content_helper,
        content_input,
        options,
      }: SearchParams
    ): Promise<SearchResults> {
      const { commit } = searchActionContext(ctx)
      commit.SET_SEARCH_RESULTS_STATUS('loading')
      commit.SET_SEARCH_QUERY(q)
      try {
        const { data: searchResults } = await ApiService.getSearchResults(
          q || '',
          api,
          entityType,
          deploymentRegion,
          teamName,
          start,
          size,
          content_helper,
          content_input,
          options
        )
        commit.SET_SEARCH_RESULTS(searchResults)
        return searchResults
      } finally {
        commit.SET_SEARCH_RESULTS_STATUS('success')
      }
    },
  }),
})

export default searchModule
